import {
  clipPathDetails,
  PATH_DETAILS_PROP,
  type PathDetails,
  readPathDetailsParts,
} from '@shared/colorizers/colorize.js';
import {
  cumulativeDistances,
  lineSegments,
  segmentDistances,
} from '@shared/geoutils.js';
import type {
  Feature,
  Geometry,
  LineString,
  MultiLineString,
  Position,
} from 'geojson';
import { type Channel, readChannels, writeChannels } from './trackChannels.js';
import type { TrackLine } from './trackSelection.js';

/**
 * Where a track is cut. Always an existing vertex: the per-point channels run
 * parallel to the coordinates, so an interpolated point would have no values.
 */
export interface TrackSplitPoint {
  featureIndex: number;
  segmentIndex: number;
  pointIndex: number;
}

/** A run of one source segment, `to` exclusive. */
interface Run {
  segment: number;
  from: number;
  to: number;
}

/** What the pieces of one edit are all cut from, read once for the lot. */
interface Source {
  segments: Position[][];
  channels: Channel[];
  /** One entry per segment, each measured from the start of its own. */
  details: PathDetails[] | undefined;
}

/** A run with a line in it; a lone point draws nothing and is dropped. */
function drawableRuns(runs: Run[]): Run[] {
  return runs.filter((run) => run.to - run.from > 1);
}

function drawableSegments(segments: Position[][]): number {
  return segments.filter((segment) => segment.length > 1).length;
}

function readSource(feature: TrackLine): Source {
  return {
    segments: lineSegments(feature.geometry),
    channels: readChannels(feature),
    details: readPathDetailsParts(feature),
  };
}

/**
 * The runs either side of a cut. Both keep the cut vertex, and a piece left
 * with a single point is dropped — so an empty side means nothing can be cut
 * here.
 */
function cutRuns(
  segments: Position[][],
  segmentIndex: number,
  pointIndex: number,
): [Run[], Run[]] {
  const segment = segments[segmentIndex];

  if (!segment) {
    return [[], []];
  }

  const whole = (offset: number) => (s: Position[], i: number) => ({
    segment: offset + i,
    from: 0,
    to: s.length,
  });

  return [
    drawableRuns([
      ...segments.slice(0, segmentIndex).map(whole(0)),
      { segment: segmentIndex, from: 0, to: pointIndex + 1 },
    ]),
    drawableRuns([
      { segment: segmentIndex, from: pointIndex, to: segment.length },
      ...segments.slice(segmentIndex + 1).map(whole(segmentIndex + 1)),
    ]),
  ];
}

/** The given runs as a feature of their own, channels cut alongside them. */
function takeRuns(
  feature: TrackLine,
  source: Source,
  runs: Run[],
): TrackLine | null {
  const kept = drawableRuns(runs);

  if (kept.length === 0) {
    return null;
  }

  const coordinates = kept.map((run) =>
    source.segments[run.segment]!.slice(run.from, run.to),
  );

  const geometry: LineString | MultiLineString =
    kept.length === 1
      ? { type: 'LineString', coordinates: coordinates[0]! }
      : { type: 'MultiLineString', coordinates };

  const properties = { ...feature.properties };

  writeChannels(
    properties,
    source.channels,
    geometry.type === 'LineString',
    (channel: Channel) =>
      kept.map((run) => channel.segments[run.segment]!.slice(run.from, run.to)),
  );

  if (source.details) {
    properties[PATH_DETAILS_PROP] = kept.map((run) => runDetails(run, source));
  }

  return { ...feature, geometry, properties };
}

/**
 * The spans of one run: the segment's own, re-based to the piece of it the run
 * is — spans are metres from where the segment starts.
 */
function runDetails(run: Run, source: Source): PathDetails {
  const details = source.details?.[run.segment] ?? {};

  const segment = source.segments[run.segment]!;

  if (run.from === 0 && run.to === segment.length) {
    return details;
  }

  const distances = cumulativeDistances(segment);

  return clipPathDetails(details, distances[run.from]!, distances[run.to - 1]!);
}

/** A track that has something to cut: a vertex with a piece on either side. */
export function isSplittable(feature: TrackLine): boolean {
  const segments = lineSegments(feature.geometry);

  return (
    segments.some((segment) => segment.length > 2) ||
    drawableSegments(segments) > 1
  );
}

/** A track that falls into more than one segment, which is what explode gives. */
export function isExplodable(feature: TrackLine): boolean {
  return drawableSegments(lineSegments(feature.geometry)) > 1;
}

/** Whether cutting at this vertex leaves a drawable piece on both sides. */
export function isCutVertex(
  feature: TrackLine,
  segmentIndex: number,
  pointIndex: number,
): boolean {
  const [head, tail] = cutRuns(
    lineSegments(feature.geometry),
    segmentIndex,
    pointIndex,
  );

  return head.length > 0 && tail.length > 0;
}

/** The coordinates of one vertex, by the indices a {@link TrackSplitPoint} names. */
export function vertexAt(
  feature: TrackLine,
  segmentIndex: number,
  pointIndex: number,
): Position | undefined {
  return lineSegments(feature.geometry)[segmentIndex]?.[pointIndex];
}

/**
 * Distance from the start of the track to each of its vertices, per segment.
 * Summed across segments as `trackEndpoints` measures it, so the gap a paused
 * recording leaves does not count.
 */
export function vertexDistances(feature: TrackLine): number[][] {
  return segmentDistances(lineSegments(feature.geometry));
}

/** The coordinates either side of a cut, for drawing the halves apart. */
export function splitTrackCoordinates(
  feature: TrackLine,
  segmentIndex: number,
  pointIndex: number,
): { head: Position[][]; tail: Position[][] } {
  const segments = lineSegments(feature.geometry);

  const [head, tail] = cutRuns(segments, segmentIndex, pointIndex);

  const coordinates = (runs: Run[]) =>
    runs.map((run) => segments[run.segment]!.slice(run.from, run.to));

  return { head: coordinates(head), tail: coordinates(tail) };
}

/**
 * The two halves a track falls into when cut at the given vertex, which both
 * halves keep. `null` where one side would be left with nothing to draw.
 */
export function splitTrackFeature(
  feature: TrackLine,
  segmentIndex: number,
  pointIndex: number,
): [TrackLine, TrackLine] | null {
  const source = readSource(feature);

  const [headRuns, tailRuns] = cutRuns(
    source.segments,
    segmentIndex,
    pointIndex,
  );

  const head = takeRuns(feature, source, headRuns);

  const tail = takeRuns(feature, source, tailRuns);

  return head && tail ? [head, tail] : null;
}

/**
 * One `LineString` feature per segment, each with the channels and path-detail
 * spans cut to it. Segments with no line in them are left out.
 */
export function trackSegmentFeatures(feature: TrackLine): TrackLine[] {
  const source = readSource(feature);

  return source.segments.flatMap((segment, i) => {
    const part = takeRuns(feature, source, [
      { segment: i, from: 0, to: segment.length },
    ]);

    return part ? [part] : [];
  });
}

/**
 * A multi-point, multi-polygon or geometry collection as one feature per
 * member, each keeping the original's properties. `null` for anything else, or
 * when there is only one member to give.
 */
export function breakApart(feature: Feature): Feature[] | null {
  const { geometry } = feature;

  const members: Geometry[] =
    geometry?.type === 'MultiPoint'
      ? geometry.coordinates.map((coordinates) => ({
          type: 'Point',
          coordinates,
        }))
      : geometry?.type === 'MultiPolygon'
        ? geometry.coordinates.map((coordinates) => ({
            type: 'Polygon',
            coordinates,
          }))
        : geometry?.type === 'GeometryCollection'
          ? geometry.geometries
          : [];

  return members.length > 1
    ? members.map((member) => ({ ...feature, geometry: member }))
    : null;
}

/**
 * A multi-segment recording as one feature per segment — the cut a paused
 * recording asks for. `null` when there is only one segment to give.
 */
export function explodeTrackFeature(feature: TrackLine): TrackLine[] | null {
  return drawableSegments(lineSegments(feature.geometry)) < 2
    ? null
    : trackSegmentFeatures(feature);
}

/**
 * The vertex nearest the given position, which is where a cut aimed there
 * lands. Compared in scaled degrees rather than metres: it runs per pointer
 * move over every point of a recording, and only the ordering matters.
 */
export function nearestTrackVertex(
  feature: TrackLine,
  lat: number,
  lon: number,
): { segmentIndex: number; pointIndex: number } | null {
  const scale = Math.cos((lat * Math.PI) / 180);

  let best: { segmentIndex: number; pointIndex: number } | null = null;

  let bestDistance = Number.POSITIVE_INFINITY;

  for (const [segmentIndex, segment] of lineSegments(
    feature.geometry,
  ).entries()) {
    for (const [pointIndex, position] of segment.entries()) {
      const dx = (position[0]! - lon) * scale;

      const dy = position[1]! - lat;

      const distance = dx * dx + dy * dy;

      if (distance < bestDistance) {
        bestDistance = distance;

        best = { segmentIndex, pointIndex };
      }
    }
  }

  return best;
}
